/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  EpisodeGuideService,
  type EpisodeInfo,
  type SeasonsData,
  type TEpisodeGuideFetchEpisodesParams,
  type TEpisodeGuideFetchSeasonsParams
} from "@tivocorp/data-domain-lib";

import type { IEpisodeGuideInteractor } from "../../interfaces/IEpisodeGuideInteractor";
import type { TNetworkOptions } from "../../types/TNetworkOptions";
import { Log } from "../../logging/Log";
import { DeviceModel } from "../../device";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";
import { ServiceLocator } from "../base/ServiceLocator";

/**
 * Filter and sort the Content Detail screen's Episodes section requests with.
 *
 * Copied verbatim from Android's `EpisodeGuideSectionLoader` constants so both
 * clients hit the same service cache key: `all` returns every episode
 * regardless of availability, and `seasonOrYearDesc` puts the newest season
 * first, which is what the season picker renders left-to-right.
 */
const EPISODE_GUIDE_FILTER: string = "all";
const EPISODE_GUIDE_SORT: string = "seasonOrYearDesc";

/**
 * Shared, atomic interactor wrapping {@link EpisodeGuideService} for the
 * Content Detail screen's "Episodes" section.
 *
 * Stateless: a fresh `EpisodeGuideService` is built per call via
 * {@link ServiceLocator}, and the {@link ApplicationFeatureArea} is fixed at
 * construction time so the `applicationFeatureArea` request header matches
 * the calling feature.
 *
 * Both endpoints additionally require an `MsoName` header. It is resolved
 * here from `DeviceModel.getTiVoSettings()` rather than being threaded down
 * from the viewModel, because it is device configuration, not screen state.
 * A blank value throws instead of issuing a request the service will reject —
 * Android treats it the same way (`ExploreEpisodeGuideViewModel.loadSeasons`
 * emits a `DomainError.Validation`).
 */
export class EpisodeGuideInteractor
  extends BaseInteractor
  implements IEpisodeGuideInteractor
{
  private static readonly TAG: string = "EpisodeGuideInteractor";

  /**
   * @param mFeatureArea - ApplicationFeatureArea reported in the request
   *                       header for every call made by this instance.
   */
  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.ALL
  ) {
    super();
  }

  /**
   * Resolves the `MsoName` header value.
   *
   * @returns The configured MSO name
   * @throws Error("Missing msoName") when the device has no MSO configured
   */
  private resolveMsoName(): string {
    const msoName: string = DeviceModel.getTiVoSettings().getMsoName().trim();
    if (msoName === "") {
      Log.w(EpisodeGuideInteractor.TAG, "resolveMsoName() — empty msoName");
      throw new Error("Missing msoName");
    }
    return msoName;
  }

  private async buildService(): Promise<EpisodeGuideService> {
    return ServiceLocator.getInstance(
      EpisodeGuideService,
      await this.buildServicesParams(
        TIVO_SERVICES.EpisodeGuideService,
        this.mFeatureArea
      )
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchSeasons(
    unifiedCollectionId: string,
    options?: TNetworkOptions
  ): Promise<SeasonsData> {
    const msoName: string = this.resolveMsoName();
    return this.executeGuarded(
      "fetchSeasons",
      async (guardedSignal: AbortSignal): Promise<SeasonsData> => {
        Log.d(EpisodeGuideInteractor.TAG, "fetchSeasons() called");
        const service: EpisodeGuideService = await this.buildService();
        const params: TEpisodeGuideFetchSeasonsParams = {
          unifiedCollectionId,
          filter:
            EPISODE_GUIDE_FILTER as TEpisodeGuideFetchSeasonsParams["filter"],
          sort: EPISODE_GUIDE_SORT as TEpisodeGuideFetchSeasonsParams["sort"],
          includeStickyInfo: false,
          hideAdult: false,
          isNewV2: false,
          assumeYear: false,
          msoName
        };
        return service.fetchSeasons(params, guardedSignal);
      },
      options
    );
  }

  /**
   * @inheritdoc
   */
  public async fetchEpisodes(
    unifiedCollectionId: string,
    seasonOrYear: number,
    limit: number,
    offset: number,
    options?: TNetworkOptions
  ): Promise<EpisodeInfo[]> {
    const msoName: string = this.resolveMsoName();
    return this.executeGuarded(
      "fetchEpisodes",
      async (guardedSignal: AbortSignal): Promise<EpisodeInfo[]> => {
        Log.d(EpisodeGuideInteractor.TAG, "fetchEpisodes() called");
        const service: EpisodeGuideService = await this.buildService();
        const params: TEpisodeGuideFetchEpisodesParams = {
          unifiedCollectionId,
          filter:
            EPISODE_GUIDE_FILTER as TEpisodeGuideFetchEpisodesParams["filter"],
          sort: EPISODE_GUIDE_SORT as TEpisodeGuideFetchEpisodesParams["sort"],
          seasonOrYear,
          hideAdult: false,
          isNewV2: false,
          limit,
          offset,
          msoName
        };
        return service.fetchEpisodes(params, guardedSignal);
      },
      options
    );
  }
}
